/**
 * useMessageQueue — Drives the fake-streaming message reveal queue.
 *
 * Reads messageQueue + revealingMessageId from the game store.
 * When the current reveal finishes, waits delayBetweenMessagesMs then advances.
 */

import { useEffect, useCallback, useRef } from 'react';
import { useGameStore } from '@/lib/stores/gameStore';
import { GAME_REVEAL_CONFIG } from '@/lib/constants/message-reveal';

export function useMessageQueue() {
  const messageQueue = useGameStore((s) => s.messageQueue);
  const revealingMessageId = useGameStore((s) => s.revealingMessageId);
  const advanceQueue = useGameStore((s) => s.advanceQueue);
  const finishReveal = useGameStore((s) => s.finishReveal);
  const skipQueue = useGameStore((s) => s.skipQueue);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stuckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Ref tracks the latest revealingMessageId to avoid stale closures in onRevealComplete.
  // Without this, the callback could capture a stale id if React re-renders between
  // the last rAF tick and the queueMicrotask flush in ProgressiveReveal.
  const revealingIdRef = useRef(revealingMessageId);
  revealingIdRef.current = revealingMessageId;

  // When no message is currently revealing and the queue has items, advance after delay
  useEffect(() => {
    if (revealingMessageId !== null || messageQueue.length === 0) {
      return;
    }
    timerRef.current = setTimeout(() => {
      advanceQueue();
    }, GAME_REVEAL_CONFIG.delayBetweenMessagesMs);

    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [revealingMessageId, messageQueue.length, advanceQueue]);

  // Safety: auto-finish stuck reveals (e.g. ProgressiveReveal unmounted before rAF fired)
  useEffect(() => {
    if (revealingMessageId === null) {
      if (stuckTimerRef.current) {
        clearTimeout(stuckTimerRef.current);
        stuckTimerRef.current = null;
      }
      return;
    }
    stuckTimerRef.current = setTimeout(() => {
      finishReveal(revealingMessageId);
    }, GAME_REVEAL_CONFIG.maxAnimationMs + 1000);

    return () => {
      if (stuckTimerRef.current) {
        clearTimeout(stuckTimerRef.current);
        stuckTimerRef.current = null;
      }
    };
  }, [revealingMessageId, finishReveal]);

  // Called by ProgressiveReveal when it finishes typing a message.
  // Reads from ref (not closure) to guarantee the latest id is used.
  const onRevealComplete = useCallback(() => {
    const id = revealingIdRef.current;
    if (id) {
      finishReveal(id);
    }
  }, [finishReveal]);

  return {
    isQueueActive: messageQueue.length > 0 || revealingMessageId !== null,
    revealingMessageId,
    onRevealComplete,
    skipQueue,
    finishReveal,
  };
}
